﻿import { Aggregators, DataGrid, EnumFormatter, Fluent, GridPersistanceFlags, PersistedGridSettings, SummaryType, toId } from "@serenity-is/corelib";
import { Column } from "@serenity-is/sleekgrid";

declare module "@serenity-is/sleekgrid" {
    interface Column<TItem = any> {
        summaryType?: import("@serenity-is/corelib").SummaryType;
    }
}

export interface CustomSummaryMixinOptions {
    grid: DataGrid<any, any>;
}

function toAggregator(summaryType: SummaryType): any {
    switch (summaryType) {
        case SummaryType.Sum: return Aggregators.Sum;
        case SummaryType.Avg: return Aggregators.Avg;
        case SummaryType.Max: return Aggregators.Max;
        case SummaryType.Min: return Aggregators.Min;
    }

    return null;
}

export class CustomSummaryMixin {

    constructor(options: CustomSummaryMixinOptions) {
        var dg = options.grid;

        if (dg.slickGrid && !dg.slickGrid.getOptions()?.showFooterRow) {
            dg.slickGrid.setFooterRowVisibility(true);
        }

        var getDefaultAggregators = () => {
            var columns = dg['allColumns'];
            var aggregators: any[] = [];
            for (var x of columns) {

                if (x.sourceItem == null)
                    continue;

                if (x.sourceItem.summaryType != null &&
                    x.sourceItem.summaryType != SummaryType.Disabled) {

                    var aggregator = toAggregator(x.sourceItem.summaryType);
                    if (aggregator == null)
                        continue;

                    x.summaryType = x.sourceItem.summaryType;
                    aggregators.push(new aggregator(x.field));
                }
            }

            return aggregators;
        };


        dg.view.setSummaryOptions({
            aggregators: getDefaultAggregators()
        });

        var aggregatesPopup = Fluent(
            <ul class="s-AggregatesPopup" style="display: none">
                {[SummaryType.Sum, SummaryType.Avg, SummaryType.Min, SummaryType.Max].map(x =>
                    <li data-agg={x}>{EnumFormatter.format(SummaryType, x)}</li>)}
            </ul>
        );

        aggregatesPopup.appendTo(document.body).on("click", "li", (e: MouseEvent) => {
            var li = (e.target as HTMLElement)?.closest?.("li");
            if (!li)
                return;

            var colId = aggregatesPopup.data("column");

            var column = (dg['allColumns'] as Column[])?.find(x => x.id == colId);
            if (!column)
                return;

            if (column.sourceItem == null ||
                column.sourceItem.summaryType == null ||
                column.sourceItem.summaryType == SummaryType.Disabled)
                return;

            var agg = toId((e.target as HTMLElement)?.dataset.agg);
            if (agg === column.summaryType)
                return;

            column.summaryType = agg;

            var aggregators = dg['allColumns']
                .filter(x => x.summaryType)
                .map(x => {
                    var aggregator = toAggregator(x.summaryType)
                    if (aggregator == null)
                        return null;

                    return new aggregator(x.field);
                }).filter(x => x != null);

            dg.view.setSummaryOptions({
                aggregators: aggregators
            });

            var grouping = (dg.view as any).getGrouping();
            for (var i = 0; i < grouping.length; i++)
                grouping[i].aggregators = aggregators;

            dg.view.setGrouping(grouping);
            dg.slickGrid.invalidate();
        });

        dg.element.on("click", ".slick-footerrow-column", (e: MouseEvent) => {
            e.preventDefault();

            var col = dg.slickGrid.getColumnFromNode((e.target as Element)?.closest('.slick-footerrow-column'));
            if (!col || !col.id)
                return;

            var column = (dg['allColumns'] as Column[])?.find(x => x.id == col.id);
            if (!column)
                return;

            if (!column ||
                !column.sourceItem ||
                column.sourceItem.summaryType == null ||
                column.sourceItem.summaryType == SummaryType.Disabled)
                return;

            aggregatesPopup.data('column', col.id).show();
            aggregatesPopup.getNode().style.top = (e.pageY - aggregatesPopup.getNode().offsetHeight) + 'px';
            aggregatesPopup.getNode().style.left = e.pageX + 'px';
            aggregatesPopup.children().forEach((li: HTMLElement) => {
                li.classList.toggle('active', li.dataset.agg == "" + column.summaryType);
            });

            let clickEv = "click.aggregatesMenu" + dg['uniqueName'];
            Fluent.off(document.body, clickEv);
            window.setTimeout(() => Fluent.on(document.body, clickEv, () => {
                aggregatesPopup.hide();
                Fluent.off(document.body, clickEv);
            }), 1);
        });

        var oldCurrentSettings = (dg as any).getCurrentSettings;
        (dg as any).getCurrentSettings = function (flags: GridPersistanceFlags) {
            var settings = oldCurrentSettings.apply(dg, [flags]);
            return settings;
        };

        var oldRestoreSettingsFrom = (dg as any).restoreSettingsFrom;
        (dg as any).restoreSettingsFrom = function (settings?: PersistedGridSettings,
            flags?: GridPersistanceFlags) {
            oldRestoreSettingsFrom.apply(dg, [settings, flags]);
        };
    }
}